/**
 * Generation Store
 *
 * Manages:
 * 1. Pending (in-progress) generations — persisted across page navigation
 *    so the "Generating..." shimmer stays visible when leaving and
 *    returning to the generation page.
 */

import { create } from 'zustand';
import { getCached, setCache, invalidateCache as invalidateCacheEntry } from './generationCacheHelpers.js';

export const useGenerationStore = create((set, get) => ({
  /**
   * Pending (in-progress) generations keyed by imageId.
   * Structure: { [imageId]: [ { generationId, imageId, userId, displayName, avatarPath, startTime } ] }
   *
   * Managed globally via realtime broadcast events in App.jsx so they survive
   * GenerationPage unmount/remount.
   */
  pendingGenerations: {},

  /**
   * In-memory cache for generation-data responses, keyed by imageId.
   * Prevents re-fetching when switching between tabs in GenerationPage.
   * Structure: { [imageId]: { generations, prompts, chatCount, fetchedAt } }
   *
   * Cache is populated on fetch and updated on unmount with the latest local
   * state. Socket events update local state in real-time; the cache is
   * refreshed when the component unmounts so the next visit sees updates.
   */
  generationDataCache: {},

  // ─── Pending generations ───

  /**
   * Add a pending generation placeholder.
   * Called from the global realtime listener when "generation-started" fires.
   */
  addPendingGeneration: (data) => {
    const { imageId } = data;
    set((state) => {
      const existing = state.pendingGenerations[imageId] || [];
      // Avoid duplicates
      if (existing.some((p) => p.generationId === data.generationId)) {
        return state;
      }
      return {
        pendingGenerations: {
          ...state.pendingGenerations,
          [imageId]: [
            {
              generationId: data.generationId,
              imageId: data.imageId,
              userId: data.userId,
              displayName: data.displayName,
              avatarPath: data.avatarPath,
              startTime: Date.now(),
            },
            ...existing,
          ],
        },
      };
    });
  },

  /**
   * Remove a single pending generation by generationId.
   * Called when "generation-complete" fires.
   */
  removePendingGeneration: (imageId, generationId) => {
    set((state) => {
      const existing = state.pendingGenerations[imageId];
      if (!existing) return state;
      const filtered = existing.filter((p) => p.generationId !== generationId);
      if (filtered.length === 0) {
        const { [imageId]: _, ...rest } = state.pendingGenerations;
        return { pendingGenerations: rest };
      }
      return {
        pendingGenerations: {
          ...state.pendingGenerations,
          [imageId]: filtered,
        },
      };
    });
  },

  /**
   * Remove ALL pending generations for an imageId.
   * Called when "generation-failed" fires (server doesn't send generationId on failure).
   */
  clearPendingForImage: (imageId) => {
    set((state) => {
      const { [imageId]: _, ...rest } = state.pendingGenerations;
      return { pendingGenerations: rest };
    });
  },

  /**
   * Get pending generations for a specific image (convenience selector).
   */
  getPendingForImage: (imageId) => {
    return get().pendingGenerations[imageId] || [];
  },

  // ─── Generation data cache ───

  /**
   * Get cached generation data for an image (if fresh enough).
   * Returns the cached entry or null.
   */
  getCachedGenerationData: (imageId) => {
    return getCached(get().generationDataCache, imageId);
  },

  /**
   * Store generation data in cache after a fetch.
   */
  setCachedGenerationData: (imageId, data) => {
    set((state) => ({
      generationDataCache: setCache(state.generationDataCache, imageId, data),
    }));
  },

  /**
   * Update the generations array and optionally the viewing generation ID
   * in the cache (called on unmount to persist state for the next tab visit).
   */
  updateCachedGenerations: (imageId, generations, viewingGenerationId, viewMode) => {
    set((state) => {
      const existing = getCached(state.generationDataCache, imageId);
      if (!existing) return state;
      const update = { ...existing, generations };
      if (viewingGenerationId !== undefined) {
        update.viewingGenerationId = viewingGenerationId;
      }
      if (viewMode !== undefined) {
        update.viewMode = viewMode;
      }
      return {
        generationDataCache: setCache(state.generationDataCache, imageId, update),
      };
    });
  },

  /**
   * Invalidate cache for a specific image (called from global event handlers
   * when generation-modifying events arrive while GenerationPage is unmounted).
   * The next mount for this imageId will re-fetch from the API.
   */
  invalidateCachedGenerationData: (imageId) => {
    set((state) => ({
      generationDataCache: invalidateCacheEntry(state.generationDataCache, imageId),
    }));
  },

  /**
   * Invalidate all cache entries (e.g., when switching projects).
   */
  clearGenerationDataCache: () => {
    set({ generationDataCache: {} });
  },
}));
